import React from 'react';

interface Card {
    id: number;
    holder_name: string;
    last4: string;
    createdAt: string;
}

interface CardSelectorProps {
    cards: Card[];
    selectedCardId: number | null;
    onCardSelect: (cardId: number) => void;
    onSelectCard: () => void;
    loading?: boolean;
}

const CardSelector: React.FC<CardSelectorProps> = ({
    cards,
    selectedCardId,
    onCardSelect,
    onSelectCard,
    loading = false,
}) => {
    if (cards.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Cards Available</h3>
                    <p className="text-gray-600">You don't have any cards linked to your account. Please add a card to continue.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                </div>
                Select Your Card
            </h2>

            <div className="space-y-3 mb-6">
                {cards.map((card) => (
                    <div
                        key={card.id}
                        onClick={() => onCardSelect(card.id)}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${selectedCardId === card.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className={`w-12 h-8 rounded-lg flex items-center justify-center mr-4 ${selectedCardId === card.id ? 'bg-[#1d4df0]' : 'bg-gray-300'
                                    }`}>
                                    <svg className="w-8 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">{card.holder_name}</p>
                                    <p className="text-xs text-gray-500">**** {card.last4}</p>
                                </div>
                            </div>
                            {selectedCardId === card.id && (
                                <div className="w-6 h-6 bg-[#1d4df0] rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <button
                onClick={onSelectCard}
                disabled={!selectedCardId || loading}
                className="w-full text-white px-6 py-3 rounded-xl hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                style={{ backgroundColor: '#1d4df0' }}
            >
                {loading ? (
                    <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                    </div>
                ) : (
                    'Continue'
                )}
            </button>
        </div>
    );
};

export default CardSelector;

